import React, { useState, useEffect } from 'react';
import {
  Card,
  Form,
  Input,
  InputNumber,
  Select,
  Button,
  Table,
  Space,
  Modal,
  message,
  Popconfirm,
  Tag,
  Typography,
  Row,
  Col,
  Statistic,
  Divider,
  AutoComplete
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SaveOutlined,
  CloseOutlined,
  SearchOutlined,
  DollarOutlined,
  StockOutlined
} from '@ant-design/icons';
import axios from 'axios';

const { Title, Text } = Typography;
const { Option } = Select;

const NHHoldingsForm = ({ onSuccess, onCancel }) => {
  const [form] = Form.useForm();
  const [holdings, setHoldings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingKey, setEditingKey] = useState('');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [stats, setStats] = useState({});
  const [stockOptions, setStockOptions] = useState([]);
  const [searchingStock, setSearchingStock] = useState(false);

  // NH투자증권 계좌 타입 옵션
  const accountTypeOptions = [
    { value: 'nh_main', label: 'NH투자증권 위탁계좌' },
    { value: 'nh_isa', label: 'NH투자증권 ISA계좌' },
    { value: 'nh_dc', label: 'NH투자증권 DC계좌' },
    { value: 'nh_pension', label: 'NH투자증권 연금계좌' },
    { value: 'nh_irp', label: 'NH투자증권 IRP계좌' }
  ];

  useEffect(() => {
    fetchHoldings();
    fetchStats();
  }, []);

  const fetchHoldings = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/data_predator/nh-holdings/', {
        headers: { Authorization: `Token ${token}` }
      });
      
      if (response.data) {
        setHoldings(response.data.results || []);
      }
    } catch (error) {
      console.error('보유 종목 조회 실패:', error);
      message.error('보유 종목을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/data_predator/nh-holdings/stats/', {
        headers: { Authorization: `Token ${token}` }
      });
      
      if (response.data) {
        setStats(response.data);
      }
    } catch (error) {
      console.error('통계 조회 실패:', error);
    }
  };

  const searchStocks = async (searchText) => {
    if (!searchText || searchText.length < 2) {
      setStockOptions([]);
      return;
    }

    try {
      setSearchingStock(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/data_predator/search-stocks/?query=${encodeURIComponent(searchText)}&limit=10`, {
        headers: { Authorization: `Token ${token}` }
      });

      if (response.data && response.data.results) {
        const options = response.data.results.map(stock => ({
          value: stock.code,
          label: `${stock.name} (${stock.code})`,
          stock_name: stock.name,
          stock_symbol: stock.code
        }));
        setStockOptions(options);
      }
    } catch (error) {
      console.error('종목 검색 실패:', error);
    } finally {
      setSearchingStock(false);
    }
  };

  const handleSubmit = async (values) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // 선택된 종목 정보 찾기
      const selectedStock = stockOptions.find(option => option.value === values.stock_symbol);
      
      const submitData = {
        account_type: values.account_type,
        stock_symbol: values.stock_symbol,
        stock_name: selectedStock ? selectedStock.stock_name : values.stock_symbol,
        quantity: values.quantity,
        avg_buy_price: values.avg_buy_price,
        is_active: values.is_active !== false
      };

      await axios.post('/api/data_predator/nh-holdings/', submitData, {
        headers: { Authorization: `Token ${token}` }
      });

      message.success('보유 종목이 성공적으로 추가되었습니다.');
      form.resetFields();
      setStockOptions([]);
      fetchHoldings();
      fetchStats();
      setIsModalVisible(false);
    } catch (error) {
      console.error('보유 종목 추가 실패:', error);
      if (error.response?.data?.error) {
        message.error(error.response.data.error);
      } else {
        message.error('보유 종목 추가에 실패했습니다.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (key, values) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      await axios.put('/api/data_predator/nh-holdings/', {
        id: key,
        ...values
      }, {
        headers: { Authorization: `Token ${token}` }
      });

      message.success('보유 종목이 성공적으로 수정되었습니다.');
      setEditingKey('');
      fetchHoldings();
      fetchStats();
    } catch (error) {
      console.error('보유 종목 수정 실패:', error);
      message.error('보유 종목 수정에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      await axios.delete('/api/data_predator/nh-holdings/', {
        data: { id },
        headers: { Authorization: `Token ${token}` }
      });

      message.success('보유 종목이 성공적으로 삭제되었습니다.');
      fetchHoldings();
      fetchStats();
    } catch (error) {
      console.error('보유 종목 삭제 실패:', error);
      message.error('보유 종목 삭제에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const isEditing = (record) => record.id === editingKey;

  const edit = (record) => {
    form.setFieldsValue({
      quantity: record.quantity,
      avg_buy_price: record.avg_buy_price,
      is_active: record.is_active,
      ...record,
    });
    setEditingKey(record.id);
  };

  const cancel = () => {
    setEditingKey('');
  };

  const save = async (key) => {
    try {
      const row = await form.validateFields();
      await handleUpdate(key, row);
    } catch (errInfo) {
      // console.log('Validate Failed:', errInfo);
    }
  };

  const columns = [
    {
      title: '계좌 타입',
      dataIndex: 'account_type_display',
      key: 'account_type_display',
      width: 150,
      render: (text, record) => (
        <Tag color="blue">{text}</Tag>
      ),
    },
    {
      title: '종목명',
      dataIndex: 'stock_name',
      key: 'stock_name',
      width: 200,
      render: (text, record) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>{text}</div>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {record.stock_symbol}
          </Text>
        </div>
      ),
    },
    {
      title: '보유 수량',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 120,
      editable: true,
      render: (text, record) => {
        if (isEditing(record)) {
          return (
            <Form.Item
              name="quantity"
              style={{ margin: 0 }}
              rules={[{ required: true, message: '수량을 입력하세요!' }]}
            >
              <InputNumber min={0} style={{ width: '100%' }} />
            </Form.Item>
          );
        }
        return <span>{text?.toLocaleString()}주</span>;
      },
    },
    {
      title: '평균 매수가',
      dataIndex: 'avg_buy_price',
      key: 'avg_buy_price',
      width: 120,
      editable: true,
      render: (text, record) => {
        if (isEditing(record)) {
          return (
            <Form.Item
              name="avg_buy_price"
              style={{ margin: 0 }}
              rules={[{ required: true, message: '평균 매수가를 입력하세요!' }]}
            >
              <InputNumber min={0} style={{ width: '100%' }} />
            </Form.Item>
          );
        }
        return <span>{text?.toLocaleString()}원</span>;
      },
    },
    {
      title: '총 매수금액',
      dataIndex: 'total_buy_amount',
      key: 'total_buy_amount',
      width: 150,
      render: (text) => (
        <span style={{ fontWeight: 'bold', color: '#1890ff' }}>
          {text?.toLocaleString()}원
        </span>
      ),
    },
    {
      title: '상태',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 80,
      editable: true,
      render: (text, record) => {
        if (isEditing(record)) {
          return (
            <Form.Item
              name="is_active"
              style={{ margin: 0 }}
              valuePropName="checked"
            >
              <Select style={{ width: '100%' }}>
                <Option value={true}>활성</Option>
                <Option value={false}>비활성</Option>
              </Select>
            </Form.Item>
          );
        }
        return (
          <Tag color={text ? 'green' : 'red'}>
            {text ? '활성' : '비활성'}
          </Tag>
        );
      },
    },
    {
      title: '수정일',
      dataIndex: 'updated_at',
      key: 'updated_at',
      width: 120,
      render: (text) => new Date(text).toLocaleDateString(),
    },
    {
      title: '작업',
      key: 'action',
      width: 120,
      render: (_, record) => {
        const editable = isEditing(record);
        return editable ? (
          <Space>
            <Button
              icon={<SaveOutlined />}
              type="primary"
              size="small"
              onClick={() => save(record.id)}
              loading={loading}
            >
              저장
            </Button>
            <Button
              icon={<CloseOutlined />}
              size="small"
              onClick={cancel}
            >
              취소
            </Button>
          </Space>
        ) : (
          <Space>
            <Button
              icon={<EditOutlined />}
              size="small"
              disabled={editingKey !== ''}
              onClick={() => edit(record)}
            >
              수정
            </Button>
            <Popconfirm
              title="정말 삭제하시겠습니까?"
              onConfirm={() => handleDelete(record.id)}
              okText="예"
              cancelText="아니오"
            >
              <Button
                icon={<DeleteOutlined />}
                size="small"
                danger
                disabled={editingKey !== ''}
              >
                삭제
              </Button>
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  return (
    <div className="layout-content">
      <Row gutter={[24, 24]}>
        {/* 헤더 */}
        <Col span={24}>
          <Card>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <StockOutlined style={{ fontSize: '24px', marginRight: '8px', color: '#52c41a' }} />
                <Title level={2} style={{ display: 'inline', margin: 0 }}>
                  NH투자증권 보유종목 관리
                </Title>
              </div>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setIsModalVisible(true)}
                size="large"
              >
                보유종목 추가
              </Button>
            </div>
            <Text style={{ color: '#666', marginTop: '8px', display: 'block' }}>
              NH투자증권 계좌의 보유 종목을 관리하고 추적할 수 있습니다.
            </Text>
          </Card>
        </Col>

        {/* 통계 카드 */}
        {stats && Object.keys(stats).length > 0 && (
          <Col span={24}>
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} md={6}>
                <Card>
                  <Statistic
                    title="총 보유종목"
                    value={stats.total_holdings || 0}
                    suffix="개"
                    valueStyle={{ color: '#1890ff' }}
                    prefix={<StockOutlined />}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Card>
                  <Statistic
                    title="활성 종목"
                    value={stats.active_holdings || 0}
                    suffix="개"
                    valueStyle={{ color: '#52c41a' }}
                    prefix={<StockOutlined />}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Card>
                  <Statistic
                    title="총 투자금액"
                    value={stats.total_investment || 0}
                    suffix="원"
                    valueStyle={{ color: '#722ed1' }}
                    prefix={<DollarOutlined />}
                    formatter={(value) => `${value.toLocaleString()}`}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Card>
                  <Statistic
                    title="평균 투자금액"
                    value={stats.avg_investment_per_stock || 0}
                    suffix="원"
                    valueStyle={{ color: '#fa8c16' }}
                    prefix={<DollarOutlined />}
                    formatter={(value) => `${Math.round(value).toLocaleString()}`}
                  />
                </Card>
              </Col>
            </Row>
          </Col>
        )}

        {/* 보유종목 테이블 */}
        <Col span={24}>
          <Card title="보유종목 목록" bordered={false}>
            <Form form={form} component={false}>
              <Table
                components={{
                  body: {
                    cell: ({ children, ...restProps }) => (
                      <td {...restProps}>
                        {children}
                      </td>
                    ),
                  },
                }}
                bordered
                dataSource={holdings}
                columns={columns}
                rowKey="id"
                loading={loading}
                pagination={{
                  pageSize: 10,
                  showSizeChanger: true,
                  showQuickJumper: true,
                  showTotal: (total, range) =>
                    `${range[0]}-${range[1]} of ${total} items`,
                }}
                scroll={{ x: 1200 }}
              />
            </Form>
          </Card>
        </Col>
      </Row>

      {/* 보유종목 추가 모달 */}
      <Modal
        title="보유종목 추가"
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          form.resetFields();
          setStockOptions([]);
        }}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="account_type"
            label="계좌 타입"
            rules={[{ required: true, message: '계좌 타입을 선택하세요!' }]}
          >
            <Select placeholder="계좌 타입을 선택하세요">
              {accountTypeOptions.map(option => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="stock_symbol"
            label="종목 검색"
            rules={[{ required: true, message: '종목을 선택하세요!' }]}
          >
            <AutoComplete
              options={stockOptions}
              onSearch={searchStocks}
              placeholder="종목명 또는 종목코드를 입력하세요"
              notFoundContent={searchingStock ? '검색 중...' : '검색 결과가 없습니다'}
              filterOption={false}
            />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="quantity"
                label="보유 수량"
                rules={[{ required: true, message: '보유 수량을 입력하세요!' }]}
              >
                <InputNumber
                  min={0}
                  style={{ width: '100%' }}
                  placeholder="보유 수량"
                  addonAfter="주"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="avg_buy_price"
                label="평균 매수가"
                rules={[{ required: true, message: '평균 매수가를 입력하세요!' }]}
              >
                <InputNumber
                  min={0}
                  style={{ width: '100%' }}
                  placeholder="평균 매수가"
                  addonAfter="원"
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="is_active"
            label="상태"
            initialValue={true}
          >
            <Select>
              <Option value={true}>활성</Option>
              <Option value={false}>비활성</Option>
            </Select>
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => {
                setIsModalVisible(false);
                form.resetFields();
                setStockOptions([]);
              }}>
                취소
              </Button>
              <Button type="primary" htmlType="submit" loading={loading}>
                추가
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default NHHoldingsForm;
